import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getReasonLabel,
  LOG_ACTION_INCOMING_GOODS_REGISTERED,
  LOG_ACTION_QUANTITY_ADJUSTED,
  LOG_ACTION_RESTOCKED_UNPICK,
  LOG_ACTION_SOLD,
} from "../../../../constants/CratesConstants";
import {
  Badge,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Input,
  List,
  message,
  Modal,
  Popover,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, writeBatch } from "firebase/firestore";
import Loading from "components/shared-components/Loading";
import BarcodeScanner from "components/shared-components/BarcodeScanner";
import { useCrateStocks } from "../../../../data/hooks/firestore/useCrateStocks";
import AddCrateStockModal from "./modals/AddCrateStockModal";
import AdjustCrateQuantityModal from "./modals/AdjustCrateQuantityModal";
import RejectProductModal from "./modals/RejectProductModal";
import ManageRejectReasonsModal from "./modals/ManageRejectReasonsModal";
import { isMobile } from "../../../../functions/browser";
import useIsMobile from "../../../../hooks/useIsMobile";
import { firestore } from "../../../../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import {
  FIRESTORE_CRATE_STOCK_LOGS_TABLE,
  FIRESTORE_CRATE_STOCKS_TABLE,
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import { updateCountedStock } from "../../../../functions/api/stock";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { StyledSelect } from "../../../../styles/sharedStyles";
import styled from "styled-components";

const STATUS_AVAILABLE = "available";
const STATUS_IN_USE = "in_use";
const CRATE_TYPE_FIXED = "fixed_dumping";
const { Text } = Typography;
const CRATE_BARCODE_MAX_LENGTH = 99;
const LOCAL_STORAGE_CRATE_CODES_KEY = "crateStocks.mockScanner.crateCodes";
const LOCAL_STORAGE_PRODUCT_CODES_KEY = "crateStocks.mockScanner.productCodes";

const renderStatusBadge = (status, t) => {
  if (status === STATUS_AVAILABLE) return <Tag color="green">{t("crate_stock.available")}</Tag>;
  if (status === STATUS_IN_USE) return <Tag color="orange">{t("crate_stock.in_use")}</Tag>;
  return <Tag>{status}</Tag>;
};

const CrateStocksTab = ({ tableY, openCrateId, onCrateDrawerOpened, onProductClick }) => {
  const { t } = useTranslation();
  const { crateStocks, isLoading, upsertCrateStock, updateCrateStock, addCrateEvent } = useCrateStocks();
  const { products } = useProducts();
  const { uid, email, profile } = useAuth();
  const isMobileView = useIsMobile();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalInitialCrateId, setAddModalInitialCrateId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCrate, setSelectedCrate] = useState(null);
  const [logModalOpen, setLogModalOpen] = useState(false);

  const [crateEvents, setCrateEvents] = useState([]);
  const [crateEventsLoading, setCrateEventsLoading] = useState(false);

  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustingCrateItem, setAdjustingCrateItem] = useState(null);

  const [expiryFilter, setExpiryFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_IN_USE);
  const [productFilter, setProductFilter] = useState(null);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [crateIdFilter, setCrateIdFilter] = useState("");
  const [fixedCrateFilter, setFixedCrateFilter] = useState(null);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [barcodeScannerEnabled, setBarcodeScannerEnabled] = useState(() => {
    const saved = localStorage.getItem("barcodeScannerEnabled");
    return saved === "true";
  });
  const [cameraScannerOpen, setCameraScannerOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [discardSubmitting, setDiscardSubmitting] = useState(false);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [manageReasonsModalOpen, setManageReasonsModalOpen] = useState(false);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [editBarcodeModalOpen, setEditBarcodeModalOpen] = useState(false);
  const [newBarcodeValue, setNewBarcodeValue] = useState("");
  const [barcodeSubmitting, setBarcodeSubmitting] = useState(false);

  useEffect(() => {
    if (!openCrateId || !crateStocks?.length) return;
    const crate = crateStocks.find((c) => c.crateId === openCrateId || c.id === openCrateId);
    if (crate) {
      setSelectedCrate(crate);
      setDrawerOpen(true);
      onCrateDrawerOpened?.();
    }
  }, [openCrateId, crateStocks]);

  const [mockScannerOpen, setMockScannerOpen] = useState(false);
  const [mockCrateCodes, setMockCrateCodes] = useState([]);
  const [mockProductCodes, setMockProductCodes] = useState([]);
  const [mockCrateCodeInput, setMockCrateCodeInput] = useState("");
  const [mockProductCodeInput, setMockProductCodeInput] = useState("");

  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);

  useEffect(() => {
    try {
      const crateCodes = JSON.parse(localStorage.getItem(LOCAL_STORAGE_CRATE_CODES_KEY) || "[]");
      const productCodes = JSON.parse(localStorage.getItem(LOCAL_STORAGE_PRODUCT_CODES_KEY) || "[]");

      setMockCrateCodes(Array.isArray(crateCodes) ? crateCodes : []);
      setMockProductCodes(Array.isArray(productCodes) ? productCodes : []);
    } catch (e) {
      setMockCrateCodes([]);
      setMockProductCodes([]);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_CRATE_CODES_KEY, JSON.stringify(mockCrateCodes || []));
    } catch (e) {
      // ignore
    }
  }, [mockCrateCodes]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_PRODUCT_CODES_KEY, JSON.stringify(mockProductCodes || []));
    } catch (e) {
      // ignore
    }
  }, [mockProductCodes]);

  const simulateScan = (code) => {
    const str = String(code || "").trim();
    if (!str) return;

    const dispatchKey = (target, key) => {
      try {
        target.dispatchEvent(
          new KeyboardEvent("keydown", {
            key,
            bubbles: true,
          }),
        );
      } catch (e) {
        // ignore
      }
    };

    // Dispatch only on document. With bubbles=true, window listeners also receive the event.
    // Dispatching on both document and window causes duplicated/garbled scans.
    [...str].forEach((k) => {
      dispatchKey(document, k);
    });
    dispatchKey(document, "Enter");
  };

  const addMockCode = (type) => {
    const raw = type === "crate" ? mockCrateCodeInput : mockProductCodeInput;
    const code = String(raw || "").trim();
    if (!code) return;
    if (!/^[0-9]+$/.test(code)) {
      message.error(t("crate_stock.code_must_be_numeric"));
      return;
    }

    if (type === "crate") {
      if (code.length > CRATE_BARCODE_MAX_LENGTH) {
        message.error(t("crate_stock.crate_code_max_n_digits", { digits: CRATE_BARCODE_MAX_LENGTH }));
        return;
      }
      if (!mockCrateCodes.includes(code)) setMockCrateCodes((prev) => [...prev, code]);
      setMockCrateCodeInput("");
      return;
    }

    if (code.length < 8 || code.length > 13) {
      message.error(t("crate_stock.product_code_must_be_8_13_digits"));
      return;
    }
    if (!mockProductCodes.includes(code)) setMockProductCodes((prev) => [...prev, code]);
    setMockProductCodeInput("");
  };

  const removeMockCode = (type, code) => {
    if (type === "crate") {
      setMockCrateCodes((prev) => (prev || []).filter((c) => c !== code));
      return;
    }
    setMockProductCodes((prev) => (prev || []).filter((c) => c !== code));
  };

  useEffect(() => {
    if (addModalOpen) return;
    if (rejectModalOpen) return;

    const isEditableElement = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const clearScanBuffer = () => {
      scanBufferRef.current = "";
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = null;
      }
    };

    const handleKeyDown = (e) => {
      if (addModalOpen) return;
      if (rejectModalOpen) return;
      if (isEditableElement(document.activeElement)) return;

      const key = e.key;

      if (/^[0-9]$/.test(key)) {
        scanBufferRef.current = `${scanBufferRef.current}${key}`.slice(-CRATE_BARCODE_MAX_LENGTH);

        if (scanTimerRef.current) {
          clearTimeout(scanTimerRef.current);
        }
        scanTimerRef.current = setTimeout(() => {
          clearScanBuffer();
        }, 300);
        return;
      }

      if (key === "Enter") {
        const code = scanBufferRef.current;
        clearScanBuffer();

        if (!code) return;
        if (code.length > CRATE_BARCODE_MAX_LENGTH) return;

        const existing = (crateStocks || []).find((c) => String(c.crateId || "").trim() === code);
        if (existing?.status === "lost") {
          message.error(t("crate_stock.crate_marked_lost"));
          return;
        }

        if (existing) {
          const cnt = typeof existing.itemsCount === "number" ? existing.itemsCount : 0;
          const isEmpty = existing.status === STATUS_AVAILABLE && cnt === 0;
          if (!isEmpty) {
            message.error(t("crate_stock.crate_not_empty"));
            return;
          }
        }

        setAddModalInitialCrateId(code);
        setAddModalOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearScanBuffer();
    };
  }, [addModalOpen, rejectModalOpen, crateStocks, t]);

  const dataSource = useMemo(() => {
    return (crateStocks || []).map((crate) => {
      const itemsArray = Array.isArray(crate.items) ? crate.items : Array.isArray(crate.products) ? crate.products : [];
      const itemsCount = typeof crate.itemsCount === "number" ? crate.itemsCount : itemsArray.length;

      const firstItem = itemsArray?.[0] || null;
      const firstProductName = firstItem?.productName || null;
      const firstProductId = firstItem?.productId || null;

      const status = crate.status || (itemsCount > 0 ? STATUS_IN_USE : STATUS_AVAILABLE);

      return {
        key: crate.id,
        ...crate,
        itemsCount,
        items: itemsArray,
        firstProductName,
        firstProductId,
        status,
      };
    });
  }, [crateStocks]);

  const getCrateExpiryStatus = useCallback(
    (crate) => {
      const itemsArray = Array.isArray(crate.items) ? crate.items : [];
      const item = itemsArray[0] || null;
      if (!item) return { overInternal: false, overShelf: false };

      const productId = item.productId;
      const product = (products || []).find((p) => p?.id === productId || p?.productId === productId) || null;

      const shelfLifeDays = product?.shelfLife != null ? Number(product.shelfLife) : null;
      const internalShelfLifeDays = product?.internalShelfLife != null ? Number(product.internalShelfLife) : null;

      const packageDateUnix =
        Number(item?.packageDateUnix || 0) || (item?.packageDate ? dayjs(item.packageDate, "DD-MM-YYYY").unix() : 0);
      if (!packageDateUnix || Number.isNaN(packageDateUnix)) return { overInternal: false, overShelf: false };

      const todayStartUnix = dayjs().startOf("day").unix();
      const ageDays = Math.floor((todayStartUnix - packageDateUnix) / 86400);

      const overShelf = typeof shelfLifeDays === "number" && !Number.isNaN(shelfLifeDays) && ageDays >= shelfLifeDays;
      const overInternal =
        typeof internalShelfLifeDays === "number" &&
        !Number.isNaN(internalShelfLifeDays) &&
        ageDays >= internalShelfLifeDays &&
        !overShelf;

      return { overInternal, overShelf, ageDays, shelfLifeDays, internalShelfLifeDays };
    },
    [products],
  );

  const filteredDataSource = useMemo(() => {
    let result = dataSource;
    if (expiryFilter) {
      result = result.filter((crate) => {
        const { overInternal, overShelf } = getCrateExpiryStatus(crate);
        if (expiryFilter === "internal") return overInternal;
        if (expiryFilter === "fully") return overShelf;
        return true;
      });
    }
    if (statusFilter) {
      result = result.filter((crate) => crate.status === statusFilter);
    }
    if (productFilter) {
      result = result.filter((crate) => {
        const itemsArray = Array.isArray(crate.items) ? crate.items : [];
        return itemsArray.some((item) => item.productId === productFilter);
      });
    }
    if (companyFilter && companyFilter !== "all") {
      result = result.filter((crate) => crate.company === companyFilter);
    }
    if (crateIdFilter) {
      const searchTerm = crateIdFilter.toLowerCase().trim();
      result = result.filter((crate) => {
        const crateId = String(crate.crateId || "").toLowerCase();
        return crateId.includes(searchTerm);
      });
    }
    if (fixedCrateFilter !== null) {
      result = result.filter((crate) => {
        const isFixed = crate.crateType === CRATE_TYPE_FIXED || crate.isFixedDumpingCrate === true;
        return fixedCrateFilter ? isFixed : !isFixed;
      });
    }
    return result;
  }, [
    dataSource,
    expiryFilter,
    statusFilter,
    productFilter,
    companyFilter,
    crateIdFilter,
    fixedCrateFilter,
    getCrateExpiryStatus,
  ]);

  const selectedCrateExpiry = useMemo(() => {
    if (!selectedCrate) return { overInternal: false, overShelf: false };
    return getCrateExpiryStatus(selectedCrate);
  }, [selectedCrate, getCrateExpiryStatus]);

  const itemsDataSource = useMemo(() => {
    const itemsArray = Array.isArray(selectedCrate?.items) ? selectedCrate.items : [];
    return itemsArray.map((item, index) => ({
      key: `${item.productId || "item"}-${index}`,
      ...item,
    }));
  }, [selectedCrate]);

  useEffect(() => {
    if (!selectedCrate?.id || !drawerOpen) return;
    const updated = (crateStocks || []).find((c) => c.id === selectedCrate.id);
    if (!updated) return;
    const itemsArray = Array.isArray(updated.items)
      ? updated.items
      : Array.isArray(updated.products)
        ? updated.products
        : [];
    const itemsCount = typeof updated.itemsCount === "number" ? updated.itemsCount : itemsArray.length;
    const firstItem = itemsArray?.[0] || null;
    const status = updated.status || (itemsCount > 0 ? STATUS_IN_USE : STATUS_AVAILABLE);
    setSelectedCrate({
      key: updated.id,
      ...updated,
      itemsCount,
      items: itemsArray,
      firstProductName: firstItem?.productName || null,
      firstProductId: firstItem?.productId || null,
      status,
    });
  }, [crateStocks, selectedCrate?.id, drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    if (!selectedCrate?.id) return;

    setCrateEventsLoading(true);

    const eventsColRef = collection(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_CRATE_STOCKS_TABLE,
      selectedCrate.id,
      "events",
    );

    const q = query(eventsColRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = [];
        snapshot.forEach((d) => {
          next.push({ id: d.id, ...d.data() });
        });
        setCrateEvents(next);
        setCrateEventsLoading(false);
      },
      () => {
        setCrateEvents([]);
        setCrateEventsLoading(false);
      },
    );

    return () => {
      unsubscribe?.();
    };
  }, [drawerOpen, selectedCrate?.id]);

  useEffect(() => {
    if (drawerOpen) return;
    setCrateEvents([]);
    setCrateEventsLoading(false);
  }, [drawerOpen]);

  const itemColumns = useMemo(() => {
    return [
      {
        title: t("crate_stock.product"),
        dataIndex: "productName",
        key: "productName",
        width: 200,
        ellipsis: true,
        render: (value, record) =>
          onProductClick ? (
            <a
              onClick={() => {
                setDrawerOpen(false);
                setSelectedCrate(null);
                onProductClick({ productId: record.productId, productName: value || record.productId });
              }}
              style={{ color: "#880e47" }}
            >
              {value || record.productId || "-"}
            </a>
          ) : (
            value || record.productId || "-"
          ),
      },
      {
        title: t("crate_stock.qty"),
        dataIndex: "quantity",
        key: "quantity",
        width: 90,
        render: (value, record) => (
          <a
            onClick={() => {
              setAdjustingCrateItem(record);
              setAdjustModalOpen(true);
            }}
            style={{
              color: "#880e47",
              ...(isMobile
                ? {
                    display: "inline-block",
                    padding: "10px 12px",
                    minHeight: 44,
                    lineHeight: "24px",
                  }
                : null),
            }}
          >
            {value ?? "-"}
          </a>
        ),
      },
      {
        title: t("crate_stock.product_id"),
        dataIndex: "productId",
        key: "productId",
        width: 140,
        ellipsis: true,
        render: (value) => value || "-",
      },
      {
        title: t("crate_stock.added_by"),
        dataIndex: "createdByName",
        key: "createdByName",
        width: 200,
        ellipsis: true,
        render: (_, record) => record?.createdByName || record?.createdBy || record?.createdByUid || "-",
      },
      {
        title: t("crate_stock.package_date"),
        dataIndex: "packageDate",
        key: "packageDate",
        width: 140,
        render: (value) => value || "-",
      },
    ];
  }, [t, setAdjustingCrateItem, setAdjustModalOpen, onProductClick]);

  const eventColumns = useMemo(() => {
    return [
      {
        title: t("crate_stock.updated"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 160,
        render: (value) => {
          if (!value) return "-";
          return dayjs.unix(Number(value)).format("DD-MM-YYYY HH:mm");
        },
      },
      {
        title: t("type"),
        dataIndex: "type",
        key: "type",
        width: 220,
        ellipsis: true,
        render: (value) => {
          const typeColors = {
            [LOG_ACTION_INCOMING_GOODS_REGISTERED]: "blue",
            [LOG_ACTION_SOLD]: "green",
            [LOG_ACTION_RESTOCKED_UNPICK]: "orange",
            [LOG_ACTION_QUANTITY_ADJUSTED]: "purple",
            DISCARD_EXPIRED: "red",
          };
          return <Tag color={typeColors[value] || "default"}>{value || "-"}</Tag>;
        },
      },
      {
        title: t("product_name"),
        dataIndex: "productName",
        key: "productName",
        width: 200,
        ellipsis: true,
        render: (value) => value || "-",
      },
      {
        title: t("crate_stock.qty_before"),
        dataIndex: "quantityBefore",
        key: "quantityBefore",
        width: 120,
        render: (value) => (value === 0 ? 0 : value || "-"),
      },
      {
        title: t("crate_stock.qty_delta"),
        dataIndex: "delta",
        key: "delta",
        width: 90,
        render: (value) => {
          if (value === 0) return "0";
          if (!value && value !== 0) return "-";
          const color = value > 0 ? "green" : "red";
          const prefix = value > 0 ? "+" : "";
          return (
            <Text style={{ color }}>
              {prefix}
              {value}
            </Text>
          );
        },
      },
      {
        title: t("crate_stock.qty"),
        dataIndex: "quantity",
        key: "quantity",
        width: 90,
        render: (value) => (value === 0 ? 0 : value || "-"),
      },
      {
        title: t("crate_stock.reason_label"),
        dataIndex: "reason",
        key: "reason",
        width: 200,
        ellipsis: true,
        render: (value, record) => {
          if (!value) return "-";
          const label = getReasonLabel(value, t);
          return record?.reasonNote ? `${label}: ${record.reasonNote}` : label;
        },
      },
      {
        title: t("order_id"),
        dataIndex: "farmorderId",
        key: "farmorderId",
        width: 140,
        ellipsis: true,
        render: (_, record) => {
          if (record?.type !== LOG_ACTION_SOLD && record?.type !== LOG_ACTION_RESTOCKED_UNPICK) return "-";
          return record?.farmorderId || record?.orderId || "-";
        },
      },
      {
        title: t("crate_stock.added_by"),
        dataIndex: "createdByName",
        key: "createdByName",
        width: 200,
        ellipsis: true,
        render: (_, record) =>
          record?.createdByName || record?.createdBy || record?.userEmail || record?.createdByUid || "-",
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: t("crate_stock.crate_id"),
        dataIndex: "crateId",
        key: "crateId",
        sorter: (a, b) => (a.crateId || "").localeCompare(b.crateId || ""),
        render: (value, record) => {
          const { overInternal, overShelf } = getCrateExpiryStatus(record);
          const isFixed = record.crateType === CRATE_TYPE_FIXED;
          return (
            <button
              className={"text-button"}
              onClick={() => {
                setSelectedCrate(record);
                setDrawerOpen(true);
              }}
            >
              <Space size={4} align="center">
                {overShelf && <Badge status="error" />}
                {overInternal && !overShelf && <Badge status="warning" />}
                <span style={{ display: "block", marginRight: 6 }}>{value}</span>
                {isFixed && <Tag color="default">Fixed</Tag>}
              </Space>
            </button>
          );
        },
      },
      {
        title: t("crate_stock.product"),
        dataIndex: "firstProductName",
        key: "firstProductName",
        width: 220,
        ellipsis: true,
        render: (value, record) => {
          const itemsArray = Array.isArray(record.items) ? record.items : [];
          const uniqueProductIds = new Set(itemsArray.map((item) => item.productId).filter(Boolean));
          if (uniqueProductIds.size > 1) {
            return t("crate_stock.mixed");
          }
          return value || record.firstProductId || "-";
        },
      },
      {
        title: <span style={{ whiteSpace: "nowrap" }}>{t("crate_stock.status")}</span>,
        dataIndex: "status",
        key: "status",
        width: 120,
        ellipsis: true,
        render: (value) => renderStatusBadge(value, t),
      },
    ];

    if (!isMobile) {
      baseColumns.push(
        {
          title: t("crate_stock.items"),
          dataIndex: "itemsCount",
          key: "itemsCount",
          width: 70,
        },
        {
          title: t("crate_stock.packed"),
          dataIndex: "items",
          key: "packageDate",
          sorter: (a, b) => {
            const aDate = a.items?.[0]?.packageDateUnix || 0;
            const bDate = b.items?.[0]?.packageDateUnix || 0;
            return aDate - bDate;
          },
          render: (items) => {
            const packageDateUnix = items?.[0]?.packageDateUnix;
            if (!packageDateUnix) return "-";
            return dayjs.unix(Number(packageDateUnix)).format("DD-MM-YYYY");
          },
        },
      );
    }

    return baseColumns;
  }, [t, getCrateExpiryStatus]);

  const handleEditBarcode = async () => {
    if (!selectedCrate) return;
    const trimmedBarcode = newBarcodeValue.trim();
    if (!trimmedBarcode) {
      message.error(t("crate_stock.barcode_required"));
      return;
    }
    if (!/^[0-9]+$/.test(trimmedBarcode)) {
      message.error(t("crate_stock.code_must_be_numeric"));
      return;
    }
    if (trimmedBarcode.length > CRATE_BARCODE_MAX_LENGTH) {
      message.error(t("crate_stock.crate_code_max_n_digits", { digits: CRATE_BARCODE_MAX_LENGTH }));
      return;
    }

    const existing = (crateStocks || []).find(
      (c) => String(c.crateId || "").trim() === trimmedBarcode && c.id !== selectedCrate.id,
    );
    if (existing) {
      message.error(t("crate_stock.barcode_already_exists"));
      return;
    }

    setBarcodeSubmitting(true);
    try {
      await updateCrateStock(selectedCrate.id, { crateId: trimmedBarcode }, uid || email || null);
      message.success(t("crate_stock.barcode_updated"));
      setEditBarcodeModalOpen(false);
      setNewBarcodeValue("");
    } catch (e) {
      console.error("Error updating barcode:", e);
      message.error(t("crate_stock.barcode_update_error"));
    } finally {
      setBarcodeSubmitting(false);
    }
  };

  const handleBarcodeScan = (scannedValue) => {
    if (scannedValue) {
      setCrateIdFilter(scannedValue);
      message.success(t("notifications.barcode_scanned"));
    }
  };

  const handleCameraBarcodeScan = (scannedValue) => {
    if (scannedValue) {
      simulateScan(scannedValue);
      message.success(t("notifications.barcode_scanned"));
    }
  };

  // TODO: Dubbele code verwijderen, kan in crateStock.js
  const handleDiscardExpiredStock = async () => {
    if (!selectedCrate) return;
    setDiscardSubmitting(true);
    try {
      const crateDocId = selectedCrate.id;
      const items = Array.isArray(selectedCrate.items) ? selectedCrate.items : [];
      const userName =
        profile?.displayName || profile?.name || profile?.fullName || profile?.email || email || uid || null;

      const eventsColRef = collection(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_CRATE_STOCKS_TABLE,
        crateDocId,
        "events",
      );
      const eventsSnap = await getDocs(eventsColRef);
      const events = [];
      eventsSnap.forEach((d) => events.push({ id: d.id, ...d.data() }));

      const discardEvent = {
        type: "DISCARD_EXPIRED",
        crateId: selectedCrate.crateId,
        itemsDiscarded: items.map((i) => ({
          productId: i.productId,
          productName: i.productName || null,
          quantityBefore: i.quantity,
          quantity: 0,
          delta: -i.quantity,
        })),
        company: selectedCrate.company || null,
        createdByUid: uid || null,
        createdBy: email || null,
        createdAt: dayjs().unix(),
      };

      events.push(discardEvent);

      if (events.length) {
        const logColRef = collection(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_CRATE_STOCK_LOGS_TABLE,
        );
        await addDoc(logColRef, {
          crateId: selectedCrate.crateId || crateDocId,
          crateDocId,
          company: selectedCrate.company || null,
          reason: "discard_expired",
          events,
          createdAt: dayjs().unix(),
          createdBy: email || null,
          createdByUid: uid || null,
        });
      }

      const batch = writeBatch(firestore);
      eventsSnap.forEach((d) => batch.delete(d.ref));
      await batch.commit();

      await updateCrateStock(crateDocId, { status: STATUS_AVAILABLE, items: [], itemsCount: 0 }, uid || email || null);

      const dateId = dayjs().format("DDMMYYYY");
      for (const item of items) {
        if (!item.packageDate || !item.productId || !(Number(item.quantity) > 0)) continue;
        try {
          const token = await getFirebaseIDToken();
          const stockDocRef = doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_PRODUCTS_STOCK_TABLE,
            dateId,
          );
          const stockDoc = await getDoc(stockDocRef);
          const currentCountedStock = Number(stockDoc.data()?.products?.[item.productId]?.countedStock || 0);
          const nextCountedStock = Math.max(0, currentCountedStock - Number(item.quantity));
          // trackAddedRemoved = false because this is discarded/expired stock, not incoming goods
          await updateCountedStock(item.productId, dateId, nextCountedStock, COMPANY_COUNTRY_CODE, token, false);
        } catch (stockError) {
          console.error("Error updating product stock:", stockError);
          message.warning(t("crate_stock.stock_update_failed_server"));
        }
      }

      message.success(t("crate_stock.discard_success"));
      setDiscardConfirmOpen(false);
      setDrawerOpen(false);
      setSelectedCrate(null);
    } catch (e) {
      console.error("Error discarding expired stock:", e);
      message.error(t("crate_stock.discard_error"));
    } finally {
      setDiscardSubmitting(false);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 8,
        }}
      >
        {!isMobileView ? (
          <Space wrap>
            <StyledSelect
              placeholder={t("crate_stock.filter_company_placeholder")}
              style={{ width: 160 }}
              $isFiltered={companyFilter !== "all"}
              value={companyFilter}
              onChange={(val) => setCompanyFilter(val || "all")}
              options={[
                { value: "all", label: t("all_companies") },
                { value: "GR", label: t("growy") },
                { value: "CF", label: t("chefs_farm") },
              ]}
            />
            <Input
              placeholder={t("crate_stock.filter_crate_id_placeholder")}
              style={{ width: 160 }}
              value={crateIdFilter}
              onChange={(e) => setCrateIdFilter(e.target.value)}
              allowClear
            />
            <StyledSelect
              allowClear
              placeholder={t("crate_stock.filter_expiry_placeholder")}
              style={{ width: 160 }}
              $isFiltered={!!expiryFilter}
              value={expiryFilter}
              onChange={(val) => setExpiryFilter(val || null)}
              options={[
                { value: "internal", label: t("crate_stock.filter_internal_expired") },
                { value: "fully", label: t("crate_stock.filter_fully_expired") },
              ]}
            />
            <StyledSelect
              allowClear
              placeholder={t("crate_stock.filter_status_placeholder")}
              style={{ width: 140 }}
              $isFiltered={!!statusFilter}
              value={statusFilter}
              onChange={(val) => setStatusFilter(val || null)}
              options={[
                { value: STATUS_AVAILABLE, label: t("crate_stock.available") },
                { value: STATUS_IN_USE, label: t("crate_stock.in_use") },
              ]}
            />
            <StyledSelect
              allowClear
              showSearch
              placeholder={t("crate_stock.filter_product_placeholder")}
              style={{ width: 200 }}
              $isFiltered={!!productFilter}
              value={productFilter}
              onChange={(val) => setProductFilter(val || null)}
              filterOption={(input, option) => {
                const searchText = option?.label || "";
                return searchText.toLowerCase().includes(input.toLowerCase());
              }}
              options={(products || []).map((product) => ({
                value: product.id || product.productId,
                label: product.name?.en || product.productName || product.productId || product.id,
              }))}
            />
            <StyledSelect
              allowClear
              placeholder={t("crate_stock.filter_fixed_crate_placeholder")}
              style={{ width: 160 }}
              $isFiltered={fixedCrateFilter !== null}
              value={fixedCrateFilter}
              onChange={(val) => setFixedCrateFilter(val === undefined ? null : val)}
              options={[
                { value: true, label: t("crate_stock.fixed_dumping_crates_only") },
                { value: false, label: t("crate_stock.regular_crates_only") },
              ]}
            />
          </Space>
        ) : (
          <Popover
            content={
              <FilterPopoverContent>
                <div>
                  <FilterLabel>{t("crate_stock.company")}</FilterLabel>
                  <Select
                    placeholder={t("crate_stock.filter_company_placeholder")}
                    style={{ width: "100%" }}
                    value={companyFilter}
                    onChange={(val) => setCompanyFilter(val || "all")}
                    size="large"
                  >
                    <Select.Option value="all">{t("all_companies")}</Select.Option>
                    <Select.Option value="GR">{t("growy")}</Select.Option>
                    <Select.Option value="CF">{t("chefs_farm")}</Select.Option>
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.crate_id")}</FilterLabel>
                  <Input.Group compact style={{ display: "flex", width: "100%" }}>
                    <Input
                      placeholder={t("crate_stock.filter_crate_id_placeholder")}
                      style={{ flex: 1 }}
                      value={crateIdFilter}
                      onChange={(e) => setCrateIdFilter(e.target.value)}
                      size="large"
                      allowClear
                    />
                    <Button size="large" icon={<QrcodeOutlined />} onClick={() => setBarcodeScannerOpen(true)} />
                  </Input.Group>
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.filter_expiry_placeholder")}</FilterLabel>
                  <Select
                    placeholder={t("crate_stock.filter_expiry_placeholder")}
                    style={{ width: "100%" }}
                    value={expiryFilter}
                    onChange={(val) => setExpiryFilter(val || null)}
                    size="large"
                  >
                    <Select.Option value="internal">{t("crate_stock.filter_internal_expired")}</Select.Option>
                    <Select.Option value="fully">{t("crate_stock.filter_fully_expired")}</Select.Option>
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.status")}</FilterLabel>
                  <Select
                    placeholder={t("crate_stock.filter_status_placeholder")}
                    style={{ width: "100%" }}
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val || null)}
                    size="large"
                  >
                    <Select.Option value={STATUS_AVAILABLE}>{t("crate_stock.available")}</Select.Option>
                    <Select.Option value={STATUS_IN_USE}>{t("crate_stock.in_use")}</Select.Option>
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.product")}</FilterLabel>
                  <Select
                    showSearch
                    placeholder={t("crate_stock.filter_product_placeholder")}
                    style={{ width: "100%" }}
                    value={productFilter}
                    onChange={(val) => setProductFilter(val || null)}
                    filterOption={(input, option) => {
                      const searchText = option?.children || "";
                      return searchText.toLowerCase().includes(input.toLowerCase());
                    }}
                    size="large"
                  >
                    {(products || []).map((product) => (
                      <Select.Option key={product.id || product.productId} value={product.id || product.productId}>
                        {product.name?.en || product.productName || product.productId || product.id}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.crate_type")}</FilterLabel>
                  <Select
                    allowClear
                    placeholder={t("crate_stock.filter_fixed_crate_placeholder")}
                    style={{ width: "100%" }}
                    value={fixedCrateFilter}
                    onChange={(val) => setFixedCrateFilter(val === undefined ? null : val)}
                    size="large"
                  >
                    <Select.Option value={true}>{t("crate_stock.fixed_dumping_crates_only")}</Select.Option>
                    <Select.Option value={false}>{t("crate_stock.regular_crates_only")}</Select.Option>
                  </Select>
                </div>
                <FilterButtonsContainer>
                  <Button
                    onClick={() => {
                      setCrateIdFilter("");
                      setCompanyFilter("all");
                      setExpiryFilter(null);
                      setStatusFilter(null);
                      setProductFilter(null);
                      setFixedCrateFilter(null);
                    }}
                    style={{ flex: 1 }}
                  >
                    {t("crate_stock.reset_filters")}
                  </Button>
                  <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                    {t("crate_stock.apply_filters")}
                  </Button>
                </FilterButtonsContainer>
              </FilterPopoverContent>
            }
            title={t("filters")}
            trigger="click"
            open={filterPopoverVisible}
            onOpenChange={setFilterPopoverVisible}
            placement="bottomLeft"
          >
            <Button
              icon={<FilterOutlined />}
              style={{
                borderColor:
                  crateIdFilter ||
                  companyFilter !== "all" ||
                  expiryFilter ||
                  statusFilter ||
                  productFilter ||
                  fixedCrateFilter !== null
                    ? "#FF4B33"
                    : "",
                color:
                  crateIdFilter ||
                  companyFilter !== "all" ||
                  expiryFilter ||
                  statusFilter ||
                  productFilter ||
                  fixedCrateFilter !== null
                    ? "#FF4B33"
                    : "",
              }}
            />
          </Popover>
        )}
        <Space>
          <Button onClick={() => setRejectModalOpen(true)}>{t("crate_stock.reject_products")}</Button>
          <Button
            type="primary"
            onClick={() => {
              setAddModalInitialCrateId(null);
              setAddModalOpen(true);
            }}
          >
            {t("crate_stock.add_products")}
          </Button>
        </Space>
      </div>

      {process.env.REACT_APP_ENVIRONMENT !== "production" && (
        <Button
          type="primary"
          shape="circle"
          icon={<QrcodeOutlined />}
          onClick={() => setMockScannerOpen(true)}
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 2000,
            width: 56,
            height: 56,
            boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
          }}
        />
      )}

      {barcodeScannerEnabled && (
        <Button
          type="primary"
          shape="circle"
          icon={<ScanOutlined />}
          onClick={() => setCameraScannerOpen(true)}
          style={{
            position: "fixed",
            right: 16,
            bottom: process.env.REACT_APP_ENVIRONMENT !== "production" ? 88 : 16,
            zIndex: 2000,
            width: 56,
            height: 56,
            boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
          }}
        />
      )}

      <div className="table-responsive">
        <CratesTable
          showSorterTooltip={false}
          columns={columns}
          dataSource={filteredDataSource}
          rowKey="key"
          sticky
          scroll={{ x: "max-content", y: tableY }}
          pagination={false}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("crate_stock.no_crates")} />,
          }}
        />
      </div>

      <AddCrateStockModal
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          setAddModalInitialCrateId(null);
        }}
        crateStocks={crateStocks}
        upsertCrateStock={upsertCrateStock}
        updateCrateStock={updateCrateStock}
        addCrateEvent={addCrateEvent}
        mode={isMobile ? "mobile" : "desktop"}
        initialCrateId={addModalInitialCrateId}
      />

      <AdjustCrateQuantityModal
        open={adjustModalOpen}
        onCancel={() => {
          setAdjustModalOpen(false);
          setAdjustingCrateItem(null);
        }}
        crate={selectedCrate}
        crateItem={adjustingCrateItem}
        updateCrateStock={updateCrateStock}
        addCrateEvent={addCrateEvent}
      />

      <RejectProductModal open={rejectModalOpen} onCancel={() => setRejectModalOpen(false)} />

      <ManageRejectReasonsModal open={manageReasonsModalOpen} onCancel={() => setManageReasonsModalOpen(false)} />

      <Modal
        title={t("crate_stock.mock_scanner")}
        open={mockScannerOpen}
        onCancel={() => setMockScannerOpen(false)}
        footer={null}
        maskClosable
        zIndex={3000}
      >
        <Tabs
          items={[
            {
              key: "crates",
              label: t("crate_stock.crates"),
              children: (
                <>
                  <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
                    <Input
                      value={mockCrateCodeInput}
                      onChange={(e) => setMockCrateCodeInput(e.target.value)}
                      placeholder={t("crate_stock.n_digit_crate_code_placeholder", {
                        digits: CRATE_BARCODE_MAX_LENGTH,
                      })}
                      inputMode="numeric"
                      maxLength={CRATE_BARCODE_MAX_LENGTH}
                      onPressEnter={() => addMockCode("crate")}
                    />
                    <Button icon={<PlusOutlined />} onClick={() => addMockCode("crate")} />
                  </Space.Compact>

                  <List
                    dataSource={mockCrateCodes}
                    locale={{ emptyText: t("crate_stock.no_crate_codes") }}
                    renderItem={(code) => (
                      <List.Item
                        actions={[
                          <Button
                            key="scan"
                            type="primary"
                            onClick={() => {
                              setMockScannerOpen(false);
                              simulateScan(code);
                            }}
                          >
                            {t("crate_stock.scan")}
                          </Button>,
                          <Button
                            key="delete"
                            icon={<DeleteOutlined />}
                            onClick={() => removeMockCode("crate", code)}
                          />,
                        ]}
                      >
                        <Text>{code}</Text>
                      </List.Item>
                    )}
                  />
                </>
              ),
            },
            {
              key: "products",
              label: t("crate_stock.products"),
              children: (
                <>
                  <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
                    <Input
                      value={mockProductCodeInput}
                      onChange={(e) => setMockProductCodeInput(e.target.value)}
                      placeholder={t("crate_stock.product_code_8_13_digits_placeholder")}
                      inputMode="numeric"
                      maxLength={13}
                      onPressEnter={() => addMockCode("product")}
                    />
                    <Button icon={<PlusOutlined />} onClick={() => addMockCode("product")} />
                  </Space.Compact>

                  <List
                    dataSource={mockProductCodes}
                    locale={{ emptyText: t("crate_stock.no_product_codes") }}
                    renderItem={(code) => (
                      <List.Item
                        actions={[
                          <Button
                            key="scan"
                            type="primary"
                            onClick={() => {
                              setMockScannerOpen(false);
                              simulateScan(code);
                            }}
                          >
                            {t("crate_stock.scan")}
                          </Button>,
                          <Button
                            key="delete"
                            icon={<DeleteOutlined />}
                            onClick={() => removeMockCode("product", code)}
                          />,
                        ]}
                      >
                        <Text>{code}</Text>
                      </List.Item>
                    )}
                  />
                </>
              ),
            },
          ]}
        />
      </Modal>

      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div>
              {selectedCrate?.crateId ? (
                <Space>
                  {t("crate_stock.drawer_title_with_crate", { crateId: selectedCrate.crateId })}
                  <EditOutlined
                    style={{ cursor: "pointer", fontSize: 16, color: "#880e47" }}
                    onClick={() => {
                      setNewBarcodeValue(selectedCrate.crateId || "");
                      setEditBarcodeModalOpen(true);
                    }}
                  />
                </Space>
              ) : (
                t("crate_stock.crate")
              )}
            </div>
            <Space>
              <Text
                style={{
                  fontWeight: 500,
                }}
              >
                Fixed
              </Text>
              <Switch
                checkedChildren="Yes"
                unCheckedChildren="No"
                checked={selectedCrate?.crateType === CRATE_TYPE_FIXED || selectedCrate?.isFixedDumpingCrate}
                onChange={async (checked) => {
                  if (!selectedCrate?.id) return;
                  const newType = checked ? CRATE_TYPE_FIXED : null;
                  try {
                    await updateCrateStock(
                      selectedCrate.id,
                      { crateType: newType, isFixedDumpingCrate: checked },
                      uid || email || null,
                    );
                    message.success(
                      checked ? t("crate_stock.set_fixed_crate_status") : t("crate_stock.removed_fixed_crate_status"),
                    );
                    setSelectedCrate({ ...selectedCrate, crateType: newType, isFixedDumpingCrate: checked });
                  } catch (e) {
                    console.error("Error updating crate type:", e);
                    message.error(t("crate_stock.crate_type_update_error"));
                  }
                }}
              />
            </Space>
          </div>
        }
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCrate(null);
        }}
        width={isMobile ? "100%" : 600}
      >
        <Descriptions bordered size="small" column={1} style={{ marginBottom: 22 }}>
          <Descriptions.Item label={t("crate_stock.status")}>
            {selectedCrate?.status ? renderStatusBadge(selectedCrate.status, t) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("crate_stock.company")}>
            <Text>{selectedCrate?.company || "-"}</Text>
          </Descriptions.Item>
        </Descriptions>

        {itemsDataSource.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("crate_stock.no_items")} />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {itemsDataSource.map((item) => (
              <Descriptions title={t("crate_stock.items")} key={item.key} size="small" column={1} bordered>
                <Descriptions.Item label={t("crate_stock.product")}>
                  {onProductClick ? (
                    <a
                      onClick={() => {
                        setDrawerOpen(false);
                        setSelectedCrate(null);
                        onProductClick({
                          productId: item.productId,
                          productName: item.productName || item.productId,
                        });
                      }}
                      style={{ color: "#880e47" }}
                    >
                      {item.productName || item.productId || "-"}
                    </a>
                  ) : (
                    item.productName || item.productId || "-"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("crate_stock.qty")}>
                  <a
                    onClick={() => {
                      setAdjustingCrateItem(item);
                      setAdjustModalOpen(true);
                    }}
                    style={{ color: "#880e47" }}
                  >
                    {item.quantity ?? "-"}
                  </a>
                </Descriptions.Item>
                <Descriptions.Item label={t("crate_stock.product_id")}>{item.productId || "-"}</Descriptions.Item>
                <Descriptions.Item label={t("crate_stock.package_date")}>{item.packageDate || "-"}</Descriptions.Item>
              </Descriptions>
            ))}
          </Space>
        )}
        {(selectedCrateExpiry.overInternal || selectedCrateExpiry.overShelf) && (
          <Button
            icon={<DeleteOutlined />}
            type="primary"
            onClick={() => setDiscardConfirmOpen(true)}
            style={{ marginTop: 16 }}
          >
            {t("crate_stock.discard_expired_stock")}
          </Button>
        )}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
          <Button onClick={() => setLogModalOpen(true)}>{t("crate_stock.view_log")}</Button>
        </div>
      </Drawer>

      <Modal
        title={t("crate_stock.edit_barcode")}
        open={editBarcodeModalOpen}
        onCancel={() => {
          setEditBarcodeModalOpen(false);
          setNewBarcodeValue("");
        }}
        maskClosable={false}
        footer={
          <Space>
            <Button
              onClick={() => {
                setEditBarcodeModalOpen(false);
                setNewBarcodeValue("");
              }}
              disabled={barcodeSubmitting}
            >
              {t("cancel")}
            </Button>
            <Button type="primary" onClick={handleEditBarcode} loading={barcodeSubmitting}>
              {t("save")}
            </Button>
          </Space>
        }
      >
        <Input
          placeholder={t("crate_stock.enter_new_barcode")}
          value={newBarcodeValue}
          onChange={(e) => setNewBarcodeValue(e.target.value)}
          maxLength={CRATE_BARCODE_MAX_LENGTH}
          autoFocus
        />
      </Modal>

      <Modal
        title={t("crate_stock.discard_confirm_title")}
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        maskClosable={false}
        footer={
          <Space>
            <Button onClick={() => setDiscardConfirmOpen(false)} disabled={discardSubmitting}>
              {t("cancel")}
            </Button>
            <Button type="primary" onClick={handleDiscardExpiredStock} loading={discardSubmitting}>
              {t("crate_stock.discard_confirm_ok")}
            </Button>
          </Space>
        }
      >
        <p>{t("crate_stock.discard_confirm_content", { crateId: selectedCrate?.crateId })}</p>
        <ul style={{ paddingLeft: 20 }}>
          {(Array.isArray(selectedCrate?.items) ? selectedCrate.items : []).map((item, idx) => (
            <li key={idx}>
              {item.productName || item.productId}: <strong>{item.quantity}</strong>
            </li>
          ))}
        </ul>
        <p style={{ marginTop: 12 }}>
          <strong>
            {t("crate_stock.discard_confirm_total", {
              total: (Array.isArray(selectedCrate?.items) ? selectedCrate.items : []).reduce(
                (sum, i) => sum + Number(i.quantity || 0),
                0,
              ),
            })}
          </strong>
        </p>
      </Modal>

      <Modal
        title={t("crate_stock.log_title")}
        open={logModalOpen}
        onCancel={() => setLogModalOpen(false)}
        width="90%"
        footer={null}
      >
        <Table
          showSorterTooltip={false}
          loading={crateEventsLoading}
          tableLayout="fixed"
          columns={eventColumns}
          dataSource={(crateEvents || []).map((e) => ({ key: e.id, ...e }))}
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_log_entries")} />,
          }}
        />
      </Modal>

      {/* Barcode Scanner for filter */}
      <BarcodeScanner
        visible={barcodeScannerOpen}
        onClose={() => setBarcodeScannerOpen(false)}
        onScan={handleBarcodeScan}
        zIndex={2000}
      />

      {/* Camera Scanner FAB */}
      <BarcodeScanner
        visible={cameraScannerOpen}
        onClose={() => setCameraScannerOpen(false)}
        onScan={handleCameraBarcodeScan}
        zIndex={2000}
      />
    </>
  );
};

const CratesTable = styled(Table)`
  div.ant-table-body {
    max-height: 60vh !important;
  }
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;

export default CrateStocksTab;
